<template>
  <div class="speech-translation-history">
    <!-- 页面头部 -->
    <div class="page-header">
      <div class="header-content">
        <h2>{{ t('speechTranslationHistory.pageTitle') }}</h2>
      </div>
    </div>

    <!-- 搜索卡片 -->
    <a-card class="search-card">
      <a-form layout="inline">
        <a-form-item :label="t('speechTranslationHistory.timeRange')">
          <a-range-picker
            v-model:value="dateRange"
            show-time
            format="YYYY-MM-DD HH:mm:ss"
            :value-format="TIME_FORMAT_API"
            :placeholder="[t('speechTranslationHistory.startTime'), t('speechTranslationHistory.endTime')]"
            style="width: 360px"
          />
        </a-form-item>
        <a-form-item>
          <a-space>
            <a-button type="primary" :loading="loading" @click="handleQuery">{{ t('speechTranslationHistory.query') }}</a-button>
            <a-button @click="handleReset">{{ t('speechTranslationHistory.reset') }}</a-button>
          </a-space>
        </a-form-item>
      </a-form>
    </a-card>

    <!-- 列表卡片 -->
    <a-card :bordered="false">
      <a-table
        :columns="columns"
        :data-source="items"
        :loading="loading"
        :pagination="pagination"
        row-key="id"
        @change="handleTableChange"
      >
        <template #bodyCell="{ column, record }">
          <template v-if="column.key === 'status'">
            <a-tag :color="statusTagColor(record.status)">
              {{ statusText(record.status) }}
            </a-tag>
          </template>
          <template v-else-if="column.key === 'time_create'">
            {{ formatTime(record.time_create) }}
          </template>
          <template v-else-if="column.key === 'duration'">
            {{ formatDuration(record.duration) }}
          </template>
          <template v-else-if="column.key === 'action'">
            <a-button
              v-if="record.status === 1"
              type="link"
              size="small"
              :loading="downloadingId === record.id"
              @click="handleDownload(record)"
            >
              {{ t('speechTranslationHistory.download') }}
            </a-button>
            <span v-else>-</span>
          </template>
        </template>
      </a-table>
    </a-card>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted, computed } from 'vue'
import { useI18n } from 'vue-i18n'
import { getHistory, exportRecord, type HistoryItem, type HistoryResponse } from '../api'
import { format } from 'date-fns'
import { message } from 'ant-design-vue'

const { t } = useI18n()

const columns = computed(() => [
  { title: t('speechTranslationHistory.id'), dataIndex: 'id', key: 'id', width: 220, ellipsis: true },
  { title: t('speechTranslationHistory.spokenLanguage'), dataIndex: 'spoken_language', key: 'spoken_language', width: 100 },
  { title: t('speechTranslationHistory.duration'), key: 'duration', width: 90 },
  { title: t('speechTranslationHistory.content'), dataIndex: 'content', key: 'content', ellipsis: true },
  { title: t('speechTranslationHistory.targetCount'), dataIndex: 'target_count', key: 'target_count', width: 90 },
  { title: t('speechTranslationHistory.targetLanguages'), dataIndex: 'target_languages', key: 'target_languages', width: 140, ellipsis: true },
  { title: t('speechTranslationHistory.status'), key: 'status', width: 90 },
  { title: t('speechTranslationHistory.startTimeCol'), key: 'time_create', width: 170 },
  { title: t('speechTranslationHistory.errorInfo'), dataIndex: 'error_info', key: 'error_info', ellipsis: true },
  { title: t('speechTranslationHistory.action'), key: 'action', width: 80, fixed: 'right' }
])

const items = ref<HistoryItem[]>([])
const loading = ref(false)
const total = ref(0)
const page = ref(1)
const pageSize = ref(20)
const downloadingId = ref<string | null>(null)
/** 时间范围 [起始, 结束]，查询时按开始时间列过滤 */
const dateRange = ref<[string, string] | null>(null)

const pagination = computed(() => ({
  current: page.value,
  pageSize: pageSize.value,
  total: total.value,
  showSizeChanger: true,
  showTotal: (totalCount: number) => t('speechTranslationHistory.paginationTotal', { total: totalCount })
}))

function statusText(status: number): string {
  const map: Record<number, string> = {
    0: t('speechTranslationHistory.statusIncomplete'),
    1: t('speechTranslationHistory.statusComplete'),
    2: t('speechTranslationHistory.statusFailed')
  }
  return map[status] ?? t('speechTranslationHistory.statusUnknown')
}

function statusTagColor(status: number): string {
  const map: Record<number, string> = { 0: 'blue', 1: 'green', 2: 'red' }
  return map[status] ?? 'default'
}

function formatTime(iso: string | null) {
  if (!iso) return '-'
  try {
    return format(new Date(iso), 'yyyy-MM-dd HH:mm:ss')
  } catch (_e) {
    return iso
  }
}

function formatDuration(seconds: number) {
  if (seconds < 60) return t('speechTranslationHistory.durationSeconds', { n: seconds })
  const m = Math.floor(seconds / 60)
  const s = seconds % 60
  return s > 0 ? t('speechTranslationHistory.durationMinutesSeconds', { m, s }) : t('speechTranslationHistory.durationMinutes', { m })
}

/** 与后端约定：时间范围传 YYYY-MM-DD HH:mm:ss（无 T），与库存、展示一致 */
const TIME_FORMAT_API = 'YYYY-MM-DD HH:mm:ss'

function getTimeRangeParams(): { time_start?: string; time_end?: string } {
  const range = dateRange.value
  if (!range || !Array.isArray(range) || range.length !== 2) return {}
  const start = range[0]
  const end = range[1]
  if (start == null || end == null) return {}
  const toStr = (v: unknown): string => {
    if (typeof v === 'string') return v.trim()
    if (v && typeof (v as { format: (s: string) => string }).format === 'function') {
      return (v as { format: (s: string) => string }).format(TIME_FORMAT_API)
    }
    return ''
  }
  const time_start = toStr(start)
  const time_end = toStr(end)
  if (!time_start || !time_end) return {}
  return { time_start, time_end }
}

async function loadHistory() {
  loading.value = true
  try {
    const params: { limit: number; offset: number; time_start?: string; time_end?: string } = {
      limit: pageSize.value,
      offset: (page.value - 1) * pageSize.value
    }
    Object.assign(params, getTimeRangeParams())
    const res = (await getHistory(params)) as HistoryResponse | undefined
    items.value = res?.items ?? []
    total.value = res?.total ?? 0
  } catch {
    items.value = []
  } finally {
    loading.value = false
  }
}

function handleQuery() {
  page.value = 1
  loadHistory()
}

function handleReset() {
  dateRange.value = null
  page.value = 1
  loadHistory()
}

/** 表格变化：分页、每页条数；与后端 limit/offset 一致，total 由接口返回 */
function handleTableChange(pag: { current?: number; pageSize?: number }) {
  if (pag == null) return
  const pageSizeChanged = pag.pageSize != null && pag.pageSize !== pageSize.value
  if (pag.current != null) page.value = pag.current
  if (pag.pageSize != null) pageSize.value = pag.pageSize
  if (pageSizeChanged) page.value = 1
  loadHistory()
}

async function handleDownload(record: HistoryItem) {
  if (downloadingId.value) return
  downloadingId.value = record.id
  try {
    const blob = await exportRecord(record.id)
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `${record.id}.txt`
    a.click()
    URL.revokeObjectURL(url)
    message.success(t('speechTranslationHistory.downloadSuccess'))
  } catch (error) {
    console.error('download history record failed:', error)
  } finally {
    downloadingId.value = null
  }
}

onMounted(() => loadHistory())
</script>

<style lang="less" scoped>
.speech-translation-history {
  padding: 24px;
}

.page-header {
  margin-bottom: 24px;

  .header-content {
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 24px;

    h2 {
      margin: 0;
      font-size: 20px;
      font-weight: 600;
      flex-shrink: 0;
    }
  }
}

.search-card {
  margin-bottom: 24px;
}

:deep(.ant-table-tbody > tr > td) {
  padding: 12px 16px;
}
</style>
